import { convertLocalToUTC, downloadLibraryZip } from 'helpers/utils'
import { Col, Row, Textarea, Typography, Select, Checkbox, DatePicker, RadioGroup, Radio } from 'components'
import { Controller, useForm, useWatch } from 'react-hook-form'
import dropdownOption from 'helpers/dropdownOptions'
import styles from './deals.module.scss'
import { Grid, IconButton } from '@mui/material'
import vars from '../../vars.scss'
import downloadAllIcon from '../../assets/images/downloadAllIcon.png'
import downloadingGif from '../../assets/images/downloading.gif'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { gql, useMutation } from '@apollo/client'
import { GET_DEAL } from 'helpers/gql'
import { useParams } from 'react-router'
import DealFooter from './DealFooter'
import { useContext } from 'react'
import { DealContext } from 'context'
import AdditionalDocuments from 'components/upload/AdditionalDocuments'
import { FORM_DEAL_TYPES, STATUS, STATUS_TYPES } from 'helpers/constants'
import { convertUTCToLocal } from 'helpers/utils'
import { yupResolver } from '@hookform/resolvers/yup'
import { DealHelper } from 'helpers/deal/DealHelper'

const DealAdmin = ({ isAdmin = false, onEditDeal, loading, dealSaveSuccessRef, depositReceiptRequired }) => {
  const params = useParams()
  const { id } = params
  const { deal } = useContext(DealContext)
  const [downloading, setDownloading] = useState(false)
  const [statusChanged, setStatusChanged] = useState(false)
  const validationSchema = DealHelper.adminSectionValidation(deal.type)

  const { handleSubmit, control, setValue ,formState} = useForm({
    defaultValues: {
      adminDocuments: deal?.adminDocuments?.length
        ? deal?.adminDocuments
        : [{ fileName: '', key: null, size: null, type: '' }]
    },
    resolver: yupResolver(validationSchema)
  })
  
  const watchedIsConditional = useWatch({
    control,
    name: 'is_conditional'
  })

 useEffect(() => {
    if (watchedIsConditional === "no") {
      setValue('condition_expiry_date', null)
    }
  }, [watchedIsConditional, setValue])

  const [adminUpdateDeal, { loading: adminSaveLoading }] = useMutation(ADMIN_UPDATE_DEAL, {
    onError: (err) => {
      toast.dismiss()
      if (!err?.message?.includes('Session timeout')) {
        toast.error(err.message)
      }
      setDownloading(false)
      if (dealSaveSuccessRef) dealSaveSuccessRef.current = false
    },
    onCompleted: () => {
      if (onEditDeal && dealSaveSuccessRef && !dealSaveSuccessRef.current) {
        // updateDeal failed (validation or API error), don't show success toast
        return
      }
      toast.dismiss()
      if (statusChanged) {
        toast.success('Deal status auto updated from submitted to In Review.')
      } else {
        toast.success('Deal updated.')
      }
      setStatusChanged(false)
      if (dealSaveSuccessRef) dealSaveSuccessRef.current = false
    },
    refetchQueries: [{ query: GET_DEAL, variables: { id } }]
  })

  const [generatePreSignedUrls] = useMutation(GENERATE_PRESIGNED_URLS, {
    onError: (err) => {
      toast.dismiss()
      if (!err?.message?.includes('Session timeout')) {
        toast.error(err.message)
      }
      setDownloading(false)
    },
    onCompleted: async (data) => {
      const filesUrl = []
      data?.generatePreSignedUrlsForDealsAndListing?.map((doc) => filesUrl.push(doc))
      await downloadLibraryZip(filesUrl, `${deal?.address}.zip`)
      setDownloading(false)
    }
  })

  const onSubmit = (values) => {
    adminUpdateDeal({
      variables: {
        data: {
          id,
          admin_notes: values.admin_notes,
          status: values.status.value,
          adminDocuments: values.adminDocuments ?? [],
          checkListTasks: values?.checklist,
          deposit_receipt_required: depositReceiptRequired || false,
          ...((deal.type === FORM_DEAL_TYPES.SALE || deal.type === FORM_DEAL_TYPES.LEASE) && {
          is_conditional: values.is_conditional,
          condition_expiry_date: (values.condition_expiry_date && (values.is_conditional === "yes")) ? convertUTCToLocal(values.condition_expiry_date) : null
        })
        }
      }
    })
  }

  const handleDownloadAll = () => {
    setDownloading(true)
    const dealDocUrls = []
    deal?.documents?.map((doc) => dealDocUrls.push(doc?.path))
    deal?.additionalDocuments?.map((doc) => dealDocUrls.push(doc?.key))
    deal?.adminDocuments?.map((doc) => dealDocUrls.push(doc?.key))
    generatePreSignedUrls({
      variables: {
        data: {
          s3ObjectKeys: dealDocUrls,
          type: 'deals'
        }
      }
    })
  }

  const getActionOptions = (deal) => {
    const closeDate = convertUTCToLocal(deal.close_date)
    const currentDate = convertUTCToLocal(new Date())
    const status = deal.status === STATUS_TYPES.closed
    const settle = deal.status !== STATUS_TYPES.settled

    const adminStatusOption = [
      settle && { value: STATUS_TYPES.review, label: STATUS.review },
      settle && { value: STATUS_TYPES.attention, label: STATUS.attention },
      settle && { value: STATUS_TYPES.approved, label: STATUS.approved },
      closeDate <= currentDate && { value: STATUS_TYPES.closed, label: STATUS.closed },
      (deal.status === STATUS_TYPES.settled || status) && { value: STATUS_TYPES.settled, label: STATUS.settled }
    ]
    return adminStatusOption.filter(Boolean)
  }
  const actionOptions = getActionOptions(deal)

  return (
    <div className={styles.responsive}>
      <form onSubmit={(e) => e.preventDefault()}>
        <Col style={{ marginTop: 32 }}>
          <Typography.Heading type="5" style={{ marginBottom: 16 }}>
            Admin Section
          </Typography.Heading>
          <Row style={{ alignItems: 'center' }}>
            {downloading ? (
              <IconButton sx={{ color: vars['doc-font-color'], mr: 1 }} size="large">
                <img src={downloadingGif} alt="downloadAllIcon" height={40} width={40} />
              </IconButton>
            ) : (
              <IconButton onClick={handleDownloadAll} sx={{ color: vars['doc-font-color'], mr: 1 }} size="large">
                <img src={downloadAllIcon} alt="downloadAllIcon" height={40} width={40} />
              </IconButton>
            )}
            <Typography.Body type={2}>Download All</Typography.Body>
          </Row>

          <AdditionalDocuments isAdminSection control={control} />

          <div>
            {deal?.checkListTasks.length > 0 && (
              <Typography.Heading type="5" style={{ marginBottom: 10, marginTop: 16 }}>
                Checklist
              </Typography.Heading>
            )}
            {deal?.checkListTasks.map((checklist, index) => {
              return (
                <div>
                  <Row>
                    <Controller
                      name={`checklist.${index}`}
                      control={control}
                      defaultValue={{ id: checklist?.id, name: checklist?.name, isChecked: checklist?.isChecked }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Checkbox
                          id={checklist?.id}
                          label={checklist?.name}
                          name={checklist?.name}
                          style={{ zoom: 1.3 }}
                          error={error?.message}
                          onChange={(e) => {
                            const data = { id: checklist?.id, name: e.target.name, isChecked: e.target.checked }
                            onChange(data)
                          }}
                          checked={value?.isChecked}
                        />
                      )}
                    />
                  </Row>
                </div>
              )
            })}
          </div>
          {(deal.type === FORM_DEAL_TYPES.SALE || deal.type === FORM_DEAL_TYPES.LEASE) && (
            <Col style={{ marginTop: 20 }}>
              <Grid container sx={{ mt: 2 }}>
                <Grid xs={12} md={6} item>
                <Controller
                    name="is_conditional"
                    control={control}
                    defaultValue={deal?.is_conditional || "no"}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <RadioGroup
                        type={5}
                        label="Condtional deal"
                        onChange={onChange}
                        value={value}
                        required
                        error={error?.message}>
                        <Radio
                          lableStyle={{ marginRight: 50 }}
                          name="yes"
                          label="Yes"
                          value="yes"
                          checked={value === "yes"}
                        />
                        <Radio name="no" label="No" value="no" checked={value === "no"} />
                      </RadioGroup>
                    )}
                  />
                  </Grid>
                  {(watchedIsConditional ? watchedIsConditional === "yes" : deal?.is_conditional === "yes")  && (
                    <Grid xs={12} md={6} item>
                      <Controller
                          name="condition_expiry_date"
                          control={control}
                          defaultValue={convertUTCToLocal(deal?.condition_expiry_date) || null}
                          render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <DatePicker
                              type={5}
                              error={error?.message}
                              value={value}
                              required
                              onChange={onChange}
                              label="Expiry Date"
                              disabled={false}
                              name="condition_expiry_date"
                              style={{ width: '100%' }}
                              minDate={new Date()}
                            />
                          )}
                        />
                  </Grid>)}
                </Grid>
            </Col>)}
          <Col style={{ marginTop: 20 }}>
            <Controller
              name="status"
              control={control}
              rules={{
                required: 'Deal status is required'
              }}
              defaultValue={dropdownOption.ADMIN_STATUS_OPTIONS.find((d) => d.value === deal.status)}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select
                  type={5}
                  options={actionOptions}
                  onChange={onChange}
                  value={value}
                  error={error?.message}
                  label="Deal status"
                  placeholder="Select deal status"
                  required
                  name="status"
                />
              )}
            />
          </Col>
          <Col style={{ marginTop: 20 }}>
            <Controller
              name="admin_notes"
              control={control}
              defaultValue={deal?.admin_notes || undefined}
              render={({ field: { onChange, value } }) => (
                <Textarea
                  type={5}
                  value={value}
                  onChange={onChange}
                  label="Admin Notes"
                  name="admin_notes"
                  style={{ width: '100%', marginBottom: 16 }}
                />
              )}
            />
          </Col>
        </Col>
      </form>
      <DealFooter
        isAdmin={isAdmin}
        loading={loading}
        onSave={handleSubmit(onSubmit)}
        adminSaveLoading={adminSaveLoading}
        onEditDeal={onEditDeal}
      />
    </div>
  )
}

const ADMIN_UPDATE_DEAL = gql`
  mutation adminUpdateDeal($data: UpdateDealInput!) {
    adminUpdateDeal(data: $data)
  }
`
const GENERATE_PRESIGNED_URLS = gql`
  mutation generatePreSignedUrlsForDealsAndListing($data: PreSignedInput) {
    generatePreSignedUrlsForDealsAndListing(data: $data) {
      fileName
      preSignedUrl
    }
  }
`

export default DealAdmin
